"use client";

import {
  type HTMLAttributes,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  useEffect,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { isString, mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { Edge, Edges } from "../Edges/Edges";
import type {
  ActiveProp,
  AltRefProp,
  ChildrenProp,
  EdgesProps,
  ElevatedProp,
  OverflowHiddenProp,
  TextAlignProp,
} from "../../props";
import { mergeRefs } from "../../utils";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { Icon } from "../Icon";
import { TargetPositioner } from "../TargetPositioner";
import { Badge } from "../Badge";
import type { Timeout } from "../../types";
import { Tooltip } from "../Tooltip";

import {
  badgeContainerStyle,
  badgeStyle,
  containerStyle,
  contentStyle,
  edgeContentStyle,
  elementPlaceholderStyle,
  flexStyle,
  highlightStyle,
  labelStyle,
  infoIconStyle,
} from "./BaseInputField.css";
import { typography } from "../../theme/css/typography.css";
import { elevations } from "../../theme/css/elevations.css";

export type BaseInputFieldProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  | "onChange"
  | "children"
  | "size"
  | "onDragEnd"
  | "onDrag"
  | "onDragStart"
  | "onAnimationStart"
> &
  RecipeVariants<typeof containerStyle> &
  ActiveProp &
  EdgesProps &
  ElevatedProp &
  AltRefProp<HTMLDivElement> &
  TextAlignProp &
  OverflowHiddenProp &
  ChildrenProp & {
    trailingLast?: ReactNode;
    display?: ReactNode;
    proxy?: ReactNode;
    resetable?: boolean;
    disabled?: boolean;
    placeholder?: string;
    highlight?: boolean;
    label?: ReactNode;
    error?: boolean;
    success?: boolean;
    required?: boolean;
    info?: string;
    /**
     * Whether to float the label, this is used to determine if the label should be in a raised position.
     * The component that implements BaseInputField should handle the logic to determine if the label should be in a raised position.
     */
    labelFloat?: boolean;
    onClick?: MouseEventHandler<HTMLDivElement>;
    onReset?: MouseEventHandler<HTMLButtonElement>; // TODO
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * BaseInputField is a component that wraps around an input field and provides
 * a consistent look and feel.
 */
export const BaseInputField: FC<BaseInputFieldProps> = ({
  cursor,
  altRef,
  leading,
  leadingClassName,
  trailing,
  trailingClassName,
  trailingLast,
  elevated = false,
  error = false,
  disabled = false,
  success = false,
  placeholder,
  active,
  className,
  label,
  align,
  required = false,
  info,
  multiLine = false,
  overflowHidden = false,
  highlight = false,
  variant = "default",
  size = "default",
  width = "default",
  labelFloat = false,
  display,
  proxy,
  children,
  ref,
  ...props
}) => {
  const internalRef = useRef<HTMLDivElement>(null);

  const [isHighlighted, setIsHighlighted] = useState(highlight);
  const timeout = useRef<Timeout | null>(null);

  useEffect(() => {
    if (isHighlighted) {
      timeout.current = setTimeout(() => {
        setIsHighlighted(false);
      }, 5000);
    }
  }, [isHighlighted]);

  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    },
    [],
  );

  const handleInfoClick = (e: MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Handle focus events for floating label animation
   */

  return (
    <>
      <motion.div
        ref={mergeRefs(internalRef, altRef, ref)}
        className={mergeClasses(
          containerStyle({
            cursor: disabled ? "default" : cursor,
            disabled,
            hasHover: !disabled,
            variant,
            hasTrailingLast: !!trailingLast,
            align,
            size,
            multiLine,
            width,
            overflowHidden,
          }),
          elevated ? elevations.l : undefined,
          typography[variant === "toggle" ? "bodyL" : "bodyM"],
          className,
        )}
        {...(disabled ? { "aria-disabled": true } : {})}
        {...(multiLine ? { "data-multiline": true } : {})}
        {...(display ? { "data-display": true } : {})}
        {...(highlight && !error ? { "data-highlight": true } : {})}
        {...(active ? { "data-active": true } : {})}
        {...(label ? { "data-label": true } : {})}
        {...(error ? { "data-error": true } : {})}
        {...(!error && success ? { "data-success": true } : {})}
        {...props}
      >
        <div className={contentStyle()}>
          {proxy}
          {label &&
            (isString(label) ? (
              <Text
                variant="bodyS"
                className={labelStyle({ float: labelFloat })}
                ellipsis
                italic
              >
                {label}
                {required && (
                  <>
                    {" "}
                    <span>*</span>
                  </>
                )}
              </Text>
            ) : (
              label
            ))}
          <Flex className={mergeClasses(flexStyle({ size, height: !label }))}>
            <Edges
              className={edgeContentStyle}
              leading={leading}
              trailing={trailing}
              leadingClassName={mergeClasses(
                !!label && labelFloat ? "with-float" : undefined,
                leadingClassName,
              )}
              trailingClassName={trailingClassName}
            >
              {placeholder && (
                <div className={mergeClasses(elementPlaceholderStyle)}>
                  {placeholder}
                </div>
              )}
              {display ? display : children}
              {!placeholder && !display && !children && <span />}
            </Edges>

            {info && (
              <Tooltip value={info}>
                <Icon
                  icon="infoFilled"
                  size="s"
                  onClick={handleInfoClick}
                  color="placeholder"
                  className={infoIconStyle}
                />
              </Tooltip>
            )}

            {trailingLast && (
              <Edge className="--trailing-last">{trailingLast}</Edge>
            )}
          </Flex>
        </div>

        {/* Success */}

        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className={badgeContainerStyle}
            >
              <Badge
                variant="success"
                value={<Icon icon="checkThick" size="xs" />}
                offset="medium"
                className={badgeStyle}
                inline
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Highlight */}
      {isHighlighted && !error && (
        <TargetPositioner
          targetRef={internalRef}
          mode="fixed"
          placement="center"
          content={<div className={highlightStyle({ size })} />}
          applyDimensions
          active
        />
      )}
    </>
  );
};
